<script>
import Repositories from '@/repositories';
const OrderItemsRepository = Repositories.get('orderitems');
const ItemSuppliersRepository = Repositories.get('itemsuppliers');
import DateEditor from '@/components/widgets/popovers/date-editor.vue';
import QuantityEditor from '@/components/widgets/popovers/quantity-yes-no-editor.vue';
import dateMixin from '@/mixins/dateMixin';
import 'vue-loading-overlay/dist/vue-loading.css';
import DeleteButton from '@/components/widgets/buttons/delete-button';
import MultipleItems from '@/components/widgets/popovers/multiple-items';
import EditOrderItemModal from './modals/order-item-edition-modal.vue';

export default {
  props: {
    orderItemsList: Array,
    supplierIdPublic: String,
  },
  components: {
    DateEditor,
    QuantityEditor,
    DeleteButton,
    MultipleItems,
    EditOrderItemModal,
  },
  mixins: [dateMixin],
  data() {
    return {
      isLoadingOrderItemIdPublic: null,
      isErrorOrderItemIdPublic: null,
      lastLoadingType: null,
      orderItemForModal: null,
    };
  },
  methods: {
    acceptPriceGlobally(newPrice, orderItem) {
      if (this.isLoadingOrderItemIdPublic == null) {
        this.isLoadingOrderItemIdPublic = orderItem.arrayKey;
        this.isErrorOrderItemIdPublic = null;
        this.lastLoadingType = 'price';

        let itemSupplierApi = {
          itemIdPublic: orderItem.itemIdPublic,
          supplierIdPublic: this.supplierIdPublic,
          newPrice: newPrice,
        };

        ItemSuppliersRepository.customREST
          .updatePrice(itemSupplierApi)
          .then(() => {
            let newOrderItem = {
              ...orderItem,
              editedPrice: 0,
              isPriceEdited: false,
              itemSupplierPrice: newPrice,
            };

            OrderItemsRepository.update(newOrderItem, newOrderItem.idPublic)
              .then(() => {
                this.orderItemUpdated(newOrderItem);
                this.isLoadingOrderItemIdPublic = null;
                this.isErrorOrderItemIdPublic = null;
              })
              .catch(() => {
                this.$toast.errorToast(this.$t('errors.errorLoading'));

                this.isLoadingOrderItemIdPublic = null;
                this.isErrorOrderItemIdPublic = orderItem.arrayKey;
              });
          })
          .catch(() => {
            this.$toast.errorToast(this.$t('errors.errorLoading'));

            this.isLoadingOrderItemIdPublic = null;
            this.isErrorOrderItemIdPublic = orderItem.arrayKey;
          });
      }
    },
    acceptPriceLocally(newPrice, orderItem) {
      if (this.isLoadingOrderItemIdPublic == null) {
        this.isLoadingOrderItemIdPublic = orderItem.arrayKey;
        this.isErrorOrderItemIdPublic = null;
        this.lastLoadingType = 'price';

        let newOrderItem = {
          ...orderItem,
          editedPrice: newPrice,
          isPriceEdited: true,
        };

        OrderItemsRepository.update(newOrderItem, newOrderItem.idPublic)
          .then(() => {
            this.orderItemUpdated(newOrderItem);
            this.isLoadingOrderItemIdPublic = null;
            this.isErrorOrderItemIdPublic = null;
          })
          .catch(() => {
            this.isLoading = false;
            this.$toast.errorToast(this.$t('errors.errorLoading'));

            this.isLoadingOrderItemIdPublic = null;
            this.isErrorOrderItemIdPublic = orderItem.arrayKey;
          });
      }
    },
    setEditedDate(newDate, orderItem) {
      if (this.isLoadingOrderItemIdPublic == null) {
        this.isLoadingOrderItemIdPublic = orderItem.arrayKey;
        this.isErrorOrderItemIdPublic = null;
        this.lastLoadingType = 'date';

        let newOrderItem = {
          ...orderItem,
          receptionDate: newDate,
        };

        OrderItemsRepository.update(newOrderItem, newOrderItem.idPublic)
          .then(() => {
            this.orderItemUpdated(newOrderItem);
            this.isLoadingOrderItemIdPublic = null;
            this.isErrorOrderItemIdPublic = null;
          })
          .catch(() => {
            this.$toast.errorToast(this.$t('errors.errorLoading'));

            this.isLoadingOrderItemIdPublic = null;
            this.isErrorOrderItemIdPublic = orderItem.arrayKey;
          });
      }
    },
    deleteOrderItem(orderItem) {
      if (this.isLoadingOrderItemIdPublic == null) {
        this.isLoadingOrderItemIdPublic = orderItem.arrayKey;
        this.isErrorOrderItemIdPublic = null;
        this.lastLoadingType = 'delete';

        OrderItemsRepository.delete(orderItem.idPublic)
          .then(() => {
            this.$emit('order-item-deleted', orderItem.idPublic);
            this.isLoadingOrderItemIdPublic = null;
            this.isErrorOrderItemIdPublic = null;
          })
          .catch(() => {
            this.$toast.errorToast(this.$t('errors.errorLoading'));

            this.isLoadingOrderItemIdPublic = null;
            this.isErrorOrderItemIdPublic = orderItem.arrayKey;
          });
      }
    },
    startEditing(orderItem) {
      this.orderItemForModal = orderItem;
      this.$nextTick(() => this.$bvModal.show('edit-order-items-modal'));
    },
    modalClosed() {
      this.orderItemForModal = null;
    },
    orderItemUpdated(orderItem) {
      this.$emit('order-item-updated', orderItem);
    },
  },
  computed: {
    currencyFormatter() {
      return this.$store.state.cache.currencyFormatter;
    },
    orderItemsListComputed() {
      if (this.orderItemsList == null) {
        return [];
      }

      let newArray = this.orderItemsList.flatMap((x) => {
        let currentPrice = +Number(x.itemSupplierPrice).toFixed(5);

        if (x.isPriceEdited) {
          currentPrice = +Number(x.editedPrice).toFixed(5);
        } else {
          if (x.itemSupplierPriceThreshold != null) {
            const sortedThresholds = x.itemSupplierPriceThreshold
              .filter((threshold) => !threshold.isDeleted) // Only consider active thresholds
              .sort((a, b) => b.minimum - a.minimum);

            const applicableThreshold = sortedThresholds.find(
              (threshold) => x.totalUnits >= threshold.minimum
            );

            if (applicableThreshold) {
              currentPrice = +Number(applicableThreshold.price).toFixed(5);
            }
          }
        }

        let qty = x.totalQuantity;

        if (x.totalUnits != x.totalQuantity) {
          qty += ' (' + x.totalUnits + ' ' + x.itemSupplierUnitName + ')';
        }

        let returnValue = {
          ...x,
          subItem: false,
          gridPrice: currentPrice,
          arrayKey: x.idPublic,
          gridReceivedQuantity: x.receivedQuantity,
          gridReservedQuantity: x.reservedQuantity,
          gridReceivedReservedQuantity: x.receivedReservedQuantity,
          gridTotalQuantity: qty,
          workOrderNames: [],
        };

        if (x.editedItemCode) {
          returnValue.gridItemCode = x.editedItemCode;
        } else if (x.itemSupplierCode) {
          returnValue.gridItemCode = x.itemSupplierCode;
        } else {
          returnValue.gridItemCode = x.itemCode;
        }

        if (x.editedItemName) {
          returnValue.gridItemName = x.editedItemName;
        } else if (x.itemSupplierName) {
          returnValue.gridItemName = x.itemSupplierName;
        } else {
          returnValue.gridItemName = x.itemName;
        }

        if (
          x.workOrderItemOrderItems == null ||
          x.workOrderItemOrderItems.length == 0
        ) {
          return returnValue;
        }

        if (
          !this.splitItemsByWorkOrder ||
          x.workOrderItemOrderItems.length == 1
        ) {
          let workOrderNames = [
            ...new Set(
              x.workOrderItemOrderItems.map((item) => item.workOrderName)
            ),
          ];

          returnValue.workOrderNames = workOrderNames;
          return returnValue;
        }

        let returnArray = [];
        let receivedReservedQuantity = x.receivedReservedQuantity;

        for (let woioi of x.workOrderItemOrderItems) {
          receivedReservedQuantity -= woioi.reservedQuantity;

          returnArray.push({
            ...x,
            subItem: true,
            arrayKey: x.idPublic + '-' + woioi.idPublic,
            price: currentPrice,
            gridTotalQuantity: woioi.totalQuantity,
            gridReservedQuantity: woioi.totalQuantity,
            gridReceivedQuantity: woioi.reservedQuantity,
            gridReceivedReservedQuantity: woioi.reservedQuantity,
            workOrderNames: [woioi.workOrderName],
          });
        }

        if (x.totalUnits != x.totalQuantity) {
          returnValue.gridTotalQuantity =
            x.totalQuantity +
            '(' +
            x.totalUnits +
            ' ' +
            x.itemSupplierUnitName +
            ')';
        }

        returnValue.gridReservedQuantity = x.reservedQuantity;
        returnValue.gridReceivedQuantity = x.receivedQuantity;
        returnValue.gridReceivedReservedQuantity = receivedReservedQuantity;
        returnArray.unshift(returnValue);

        return returnArray;
      });

      return newArray;
    },
    splitItemsByWorkOrder() {
      return this.$store.state.cache.ordersConfig.splitItemsByWorkOrder;
    },
  },
};
</script>

<template>
  <div>
    <table class="table table-hover">
      <thead class="thead-light">
        <tr>
          <th class="table-row-relative padding-mofifier">
            {{ $t('general.code') }}
          </th>
          <th class="table-row-expandable max-mofifier">
            {{ $t('items.item') }}
          </th>
          <th class="table-row-relative padding-mofifier">
            {{ $t('items.receptionDate') }}
          </th>
          <th class="table-row-relative padding-mofifier">
            {{ $t('workOrders.workOrders') }}
          </th>
          <th class="table-row-relative padding-mofifier">
            {{ $t('items.price') }}
          </th>
          <th class="table-row-relative padding-mofifier">
            {{ $t('items.reserved') }}
          </th>
          <th class="table-row-relative padding-mofifier">
            {{ $t('items.received') }}
          </th>
          <th class="table-row-relative padding-mofifier">
            {{ $t('items.total') }}
          </th>
          <th class="table-row-relative padding-mofifier">
            {{ $t('general.edit') }}
          </th>
          <th class="table-row-relative padding-mofifier">
            {{ $t('general.delete') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="orderItem in orderItemsListComputed"
          :key="orderItem.arrayKey"
          :style="{
            'font-size': orderItem.subItem ? '0.9em' : '1em',
            color: orderItem.subItem ? '#7a8793' : '#495057',
          }"
        >
          <td class="table-row-relative padding-mofifier">
            {{ orderItem.subItem ? '' : orderItem.gridItemCode }}
          </td>
          <td class="table-row-expandable max-mofifier">
            {{ orderItem.subItem ? '' : orderItem.gridItemName }}
            <span
              v-if="
                orderItem.orderItemNote != null &&
                  orderItem.orderItemNote.length > 0
              "
            >
              <b-popover
                :target="'popover-orderitem-' + orderItem.arrayKey"
                triggers="hover"
                placement="top"
                variant="warning"
              >
                {{ orderItem.orderItemNote }}
              </b-popover>
              <i
                :id="'popover-orderitem-' + orderItem.arrayKey"
                style="font-size: 1.2em; cursor: pointer"
                class="fas fa-sticky-note text-warning"
              ></i>
            </span>
          </td>
          <td
            v-if="!orderItem.subItem"
            :id="'popover-edit-date-' + orderItem.arrayKey"
            class="table-row-relative padding-mofifier"
            style="cursor: pointer"
          >
            <div v-if="isLoadingOrderItemIdPublic == orderItem.arrayKey">
              <i
                class="fas fa-spinner fa-spin text-warning ml-2 mr-2"
                style="font-size: 1.3em"
              ></i>
            </div>
            <div
              v-else-if="
                isErrorOrderItemIdPublic == orderItem.arrayKey &&
                  lastLoadingType == 'date'
              "
            >
              <i
                class="fas fa-exclamation text-danger ml-2 mr-2"
                style="font-size: 1.3em"
              ></i>
            </div>
            <div v-else-if="orderItem.receptionDate != null">
              {{ formatDate(orderItem.receptionDate) }}
            </div>
            <div v-else>
              <i
                class="fas fa-plus text-success ml-2 mr-2"
                style="font-size: 1.3em"
              ></i>
            </div>
          </td>
          <td v-else></td>
          <td class="table-row-relative">
            <MultipleItems
              :list="orderItem.workOrderNames"
              :uniqueId="orderItem.arrayKey"
            />
          </td>
          <td
            v-if="!orderItem.subItem"
            :id="'popover-edit-price-' + orderItem.arrayKey"
            class="table-row-relative padding-mofifier clickable-text"
          >
            <div v-if="isLoadingOrderItemIdPublic == orderItem.arrayKey">
              <i
                class="fas fa-spinner fa-spin text-warning ml-2 mr-2"
                style="font-size: 1.3em"
              ></i>
            </div>
            <div
              v-else-if="
                isErrorOrderItemIdPublic == orderItem.arrayKey &&
                  lastLoadingType == 'price'
              "
            >
              <i
                class="fas fa-exclamation text-danger ml-2 mr-2"
                style="font-size: 1.3em"
              ></i>
            </div>
            <div v-else>
              {{ currencyFormatter.format(orderItem.gridPrice) }}
            </div>
          </td>
          <td v-else></td>
          <td class="table-row-relative padding-mofifier">
            {{ orderItem.gridReservedQuantity }}
          </td>
          <td class="table-row-relative padding-mofifier">
            {{ orderItem.gridReceivedQuantity }}
          </td>
          <td class="table-row-relative padding-mofifier">
            {{ orderItem.gridTotalQuantity }}
          </td>
          <td class="table-row-relative padding-mofifier">
            <template v-if="!orderItem.subItem">
              <i
                class="fas fa-pencil-alt text-success pl-2 pr-2"
                @click="startEditing(orderItem)"
                style="cursor: pointer; font-size: 1.3em"
              ></i>
            </template>
          </td>
          <td class="table-row-relative padding-mofifier">
            <template v-if="!orderItem.subItem">
              <DeleteButton
                :key="orderItem.arrayKey"
                :error="
                  isErrorOrderItemIdPublic == orderItem.arrayKey &&
                    lastLoadingType == 'delete'
                "
                @clicked="deleteOrderItem(orderItem)"
              />
            </template>
          </td>

          <QuantityEditor
            v-if="!orderItem.subItem"
            :target="'popover-edit-price-' + orderItem.arrayKey"
            placement="top"
            :defaultIsEditingPrice="true"
            :returnedObject="orderItem"
            :defaultQuantity="orderItem.price"
            :title="$t('general.editPrice')"
            :showGlobal="
              orderItem.itemSupplierPriceThreshold == null ||
                orderItem.itemSupplierPriceThreshold.length == 0
            "
            @accept-price-globally="acceptPriceGlobally"
            @accept-price-locally="acceptPriceLocally"
          />

          <DateEditor
            v-if="!orderItem.subItem"
            :returnedObject="orderItem"
            :target="'popover-edit-date-' + orderItem.arrayKey"
            :defaultValue="orderItem.receptionDate"
            @set-edited-date="setEditedDate"
          />

          <b-popover
            v-if="
              isErrorOrderItemIdPublic == orderItem.arrayKey &&
                lastLoadingType == 'date'
            "
            show
            :target="'popover-edit-date-' + orderItem.arrayKey"
            triggers="hover"
            placement="bottom"
            variant="danger"
          >
            <div style="font-size: 1.2em">
              {{ $t('errors.operationFailedTryAgain') }}
            </div>
          </b-popover>

          <b-popover
            v-if="
              isErrorOrderItemIdPublic == orderItem.arrayKey &&
                lastLoadingType == 'price'
            "
            show
            :target="'popover-edit-price-' + orderItem.arrayKey"
            triggers="hover"
            placement="bottom"
            variant="danger"
          >
            <div style="font-size: 1.2em">
              {{ $t('errors.operationFailedTryAgain') }}
            </div>
          </b-popover>
        </tr>
      </tbody>
    </table>

    <EditOrderItemModal
      v-if="orderItemForModal != null"
      :orderItem="orderItemForModal"
      @order-item-updated="orderItemUpdated"
    />
  </div>
</template>

<style scoped>
.padding-mofifier {
  padding-left: 10px;
  padding-right: 10px;
  min-width: 60px;
}

.max-mofifier {
  max-width: 250px;
  overflow: hidden;
}
</style>
